<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';

import usersRepository from '@/api/usersRepository';
import IconSearch from '@/components/icons/IconSearch.vue';
import { useEventBus } from '@/composables/useEventBus';
import { useCompanyStore } from '@/stores/company';
import { useUserStore } from '@/stores/user';

const companyStore = useCompanyStore();
const userStore = useUserStore();
const eventBus = useEventBus();

const isActive = ref(false);
const searchTerm = ref('');

const companies = computed(() => companyStore.companies);
const currentCompany = computed(() => userStore.currentUser?.company);

const filteredCompanies = computed(() => {
  return companies.value?.filter(company =>
    company.name.toLowerCase().includes(searchTerm.value.toLowerCase()));
});

const switchCompany = async (uuid: string) => {
  await usersRepository.switchCompany(uuid);
  eventBus.$emit('resetApplicationStates');
};

const toggleDropdown = () => {
  if (searchTerm.value.length) {
    searchTerm.value = '';
  }

  isActive.value = !isActive.value;
};

const closeDropdown = (ev: MouseEvent) => {
  const companySwitcherEl = companySwitcherRef.value;
  const clickedOutside = companySwitcherEl !== ev.target && companySwitcherEl && !companySwitcherEl.contains(ev.target as Node);

  if (isActive.value && clickedOutside) {
    isActive.value = false;
  }
};

const companySwitcherRef = ref<HTMLDivElement | null>(null);

onMounted(() => {
  window.addEventListener('click', closeDropdown);
});

onUnmounted(() => {
  window.removeEventListener('click', closeDropdown);
});
</script>

<template>
  <div
    ref="companySwitcherRef"
    class="company-switcher"
  >
    <button
      :class="{ 'company-switcher__btn--active': isActive }"
      class="company-switcher__btn"
      @click="toggleDropdown"
    >
      <span>
        {{ currentCompany?.name }}
      </span>
    </button>
    <transition name="fade">
      <div
        v-if="isActive"
        class="company-switcher__list-wrapper"
      >
        <div class="company-switcher__searchbar">
          <IconSearch class="icon" />
          <input
            v-model.trim="searchTerm"
            type="text"
            :placeholder="$t('companySwitcher.placeholder')"
          >
        </div>
        <ul
          v-if="filteredCompanies?.length"
          class="company-switcher__list"
        >
          <li
            v-for="{ name, uuid } in filteredCompanies"
            :key="uuid"
            class="company-switcher__list-item"
            @click="switchCompany(uuid)"
          >
            {{ name }}
          </li>
        </ul>
        <div
          v-else
          class="company-switcher__no-results"
        >
          {{ $t('companySwitcher.noResults', { term: searchTerm }) }}
        </div>
      </div>
    </transition>
  </div>
</template>

<style lang="scss" scoped>
.company-switcher {
  position: relative;
  z-index: 1;

  &__btn {
    background: transparent;
    border: none;
    height: 60px;
    padding: 0 20px;
    font-weight: 700;
    display: flex;
    align-items: center;

    &:hover:not(.company-switcher__btn--active) {
      background: var(--gray);
    }

    &::after {
      content: '';
      width: 0;
      height: 0;
      border-color: var(--text-color) transparent transparent;
      border-style: solid;
      border-width: 6px 5px 0;
      margin-left: 13px;
      transition: 0.3s;
    }

    &--active {
      background: var(--gray);
    }

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 180px;
      display: block;
    }
  }

  &__searchbar {
    min-width: 220px;
    padding: 10px 15px;
    background: var(--gray);
    border-radius: 3px 3px 0 0;
    display: flex;

    .icon {
      width: 20px;
      height: 20px;
    }

    input {
      width: 100%;
      margin-left: 10px;
      background: transparent;
      border: none;

      &::placeholder {
        color: var(--placeholder-color);
      }
    }
  }

  &__list-wrapper {
    position: absolute;
    top: calc(100% + 4px);
    left: 50%;
    transform: translateX(-50%);
    cursor: default;
    border: 1px solid var(--gray-dark);
    box-shadow: 0 5px 10px 0 rgb(12 30 48 / 10%);
    border-radius: 3px;
    overflow: hidden;
  }

  &__list {
    max-height: 236px;
    width: 100%;
    overflow-y: auto;
    text-align: left;
    border-radius: 0 0 3px 3px;
  }

  &__list-item {
    padding: 12px 15px;
    background-color: var(--white);
    transition: 0.3s;
    cursor: pointer;
    border-bottom: 1px solid var(--gray-dark);

    &:hover {
      color: var(--blue);
    }
  }

  &__no-results {
    padding: 12px 15px;
    border-radius: 0 0 3px 3px;
    background-color: var(--white);
    text-align: left;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
